/**
 * TimelineContainer - Container da linha do tempo horizontal
 * 
 * Features:
 * - Horizontal scroll com snap central
 * - Cards sempre alinham ao centro
 * - Inércia suave no scroll
 * - Suporte a drag (desktop) e swipe (mobile)
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import TimelineCard from './TimelineCard';

export default function TimelineContainer({
  phases = [],
  onCompare,
  onPhaseChange,
}) {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, scrollLeft: 0 });

  // Calculate which card is centered
  const updateActiveIndex = useCallback(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const scrollLeft = container.scrollLeft;
    const cardWidth = container.offsetWidth * 0.7; // 70vw
    const gap = 24; // gap between cards
    const centerOffset = (container.offsetWidth - cardWidth) / 2;
    
    const index = Math.round((scrollLeft + centerOffset) / (cardWidth + gap));
    const clampedIndex = Math.max(0, Math.min(phases.length - 1, index));
    
    if (clampedIndex !== activeIndex) {
      setActiveIndex(clampedIndex);
      onPhaseChange?.(clampedIndex);
    }
  }, [activeIndex, phases.length, onPhaseChange]);

  // Scroll event listener
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollTimeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateActiveIndex, 50);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [updateActiveIndex]);

  // Mouse drag handlers (desktop)
  const handleMouseDown = (e) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    dragStart.current = {
      x: e.pageX - containerRef.current.offsetLeft,
      scrollLeft: containerRef.current.scrollLeft,
    };
    containerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - dragStart.current.x) * 1.5; // Scroll speed multiplier
    containerRef.current.scrollLeft = dragStart.current.scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = 'grab';
    }
  };

  // Scroll to specific card
  const scrollToCard = (index) => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const cardWidth = container.offsetWidth * 0.7;
    const gap = 24;
    const centerOffset = (container.offsetWidth - cardWidth) / 2;
    const targetScroll = index * (cardWidth + gap) - centerOffset;
    
    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  };

  // Initial scroll to first card centered
  useEffect(() => {
    if (containerRef.current && phases.length > 0) {
      setTimeout(() => scrollToCard(0), 100);
    }
  }, [phases.length]);

  if (phases.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-stone-400 italic">
          Sua jornada está apenas começando...
        </p>
      </div>
    );
  }

  return (
    <div className="relative flex-1 flex flex-col justify-center overflow-hidden">
      {/* Timeline track */}
      <div
        ref={containerRef}
        className="flex gap-6 px-[15vw] py-8 overflow-x-auto scroll-smooth hide-scrollbar"
        style={{
          scrollSnapType: 'x mandatory',
          cursor: 'grab',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {phases.map((phase, index) => (
          <div
            key={phase.id || index}
            className="scroll-snap-center"
            style={{ scrollSnapAlign: 'center' }}
          >
            <TimelineCard
              period={phase.period}
              title={phase.title}
              writingStyle={phase.writingStyle}
              thought={phase.thought}
              excerpt={phase.excerpt}
              isActive={index === activeIndex}
              onCompare={() => onCompare?.(phase)}
            />
          </div>
        ))}
      </div>

      {/* Navigation dots */}
      <div className="flex justify-center gap-2 mt-4">
        {phases.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollToCard(index)}
            className={`
              w-2 h-2 rounded-full transition-all duration-300
              ${index === activeIndex 
                ? 'bg-amber-500 w-6' 
                : 'bg-stone-300 hover:bg-stone-400'
              }
            `}
            aria-label={`Ir para fase ${index + 1}`}
          />
        ))}
      </div>

      {/* Hide scrollbar styles */}
      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
